// <s-product-gallery> — one main media slot plus thumbnails. A "--show" command from a thumbnail
// ([data-gallery-thumb]) makes that slide the main one and hides its own thumbnail, so images swap places.
// A horizontal swipe on the main media ([data-product-gallery]) moves to the next or previous slide.
class SProductGallery extends HTMLElement {
  #ac;
  #index = 0;
  #startX = 0;
  #startY = 0;
  #tracking = false;
  #swiped = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.addEventListener('command', (event) => {
      if (event.command !== '--show') return;
      const index = Number(event.source?.dataset.galleryThumb);
      if (Number.isNaN(index)) return;
      this.#show(index, true);
    }, { signal });

    const stage = this.querySelector('[data-product-gallery]');
    if (!stage || this.querySelectorAll('[data-gallery-slide]').length < 2) return;
    stage.style.touchAction = 'pan-y';

    stage.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) { this.#tracking = false; return; }
      this.#startX = event.touches[0].clientX;
      this.#startY = event.touches[0].clientY;
      this.#tracking = true;
      this.#swiped = false;
    }, { passive: true, signal });

    stage.addEventListener('touchend', (event) => {
      if (!this.#tracking) return;
      this.#tracking = false;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - this.#startX;
      const dy = touch.clientY - this.#startY;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      this.#swiped = true;
      this.#step(dx < 0 ? 1 : -1);
    }, { passive: true, signal });

    stage.addEventListener('touchcancel', () => { this.#tracking = false; }, { passive: true, signal });

    // A swipe must not also open the zoom dialog.
    stage.addEventListener('click', (event) => {
      if (!this.#swiped) return;
      this.#swiped = false;
      event.preventDefault();
      event.stopPropagation();
    }, { capture: true, signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #step(delta) {
    const count = this.querySelectorAll('[data-gallery-slide]').length;
    if (count < 2) return;
    this.#show((this.#index + delta + count) % count, false);
  }

  #show(index, moveFocus) {
    const slides = [...this.querySelectorAll('[data-gallery-slide]')];
    const items = [...this.querySelectorAll('[data-gallery-thumb-item]')];
    if (!slides[index]) return;
    this.#index = index;

    slides.forEach((slide, i) => {
      const active = i === index;
      if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.hidden = !active;
    });
    items.forEach((item, i) => { item.hidden = i === index; });

    // The clicked thumbnail is now hidden, so focus moves to the new main image.
    if (moveFocus) slides[index].querySelector('button, video, iframe, [tabindex]')?.focus({ preventScroll: true });
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
